import { DAY_NAMES } from '@/constants/recurringEvent';
import {
  DaysOfTheWeek,
  RecurringEvent,
  RepeatDaysType,
} from '@/types/RecurringEvent';
import { Box, Popover } from '@mui/material';
import {
  Link,
  Typography,
  useTheme,
} from '@silverstein-properties/inspirelabs-ui';
import { useEffect, useRef, useState } from 'react';

const formatRecurringEvent = (reccuringEvent: RecurringEvent) => {
  if (reccuringEvent.repeatDays?.type === RepeatDaysType.DAILY) {
    return 'Repeats every day';
  }
  if (reccuringEvent.repeatDays?.type === RepeatDaysType.WEEKLY) {
    const daysOfWeek = reccuringEvent.repeatDays?.daysOfWeek || [];
    const dayNames = daysOfWeek
      .map((day: DaysOfTheWeek) => {
        return DAY_NAMES[day - 1] || '';
      })
      .filter(Boolean);

    if (dayNames.length === 0) {
      return 'Repeats weekly';
    }
    return `Repeats weekly on ${dayNames.join(', ')}`;
  }
  if (reccuringEvent.repeatDays?.type === RepeatDaysType.MONTHLY) {
    return `Repeats every month on the ${reccuringEvent.repeatDays?.daysOfWeek?.join(
      ', '
    )} day`;
  }
  return '';
};

export const CustomersPopover = ({
  children,
  bookings,
  capacity,
  title,
  timeRange,
  reccuringEvent,
}: {
  children: React.ReactNode;
  bookings: { consumerId: string; name: string }[];
  capacity?: number;
  timeRange?: string;
  title?: string;
  reccuringEvent?: RecurringEvent;
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const [isOverButton, setIsOverButton] = useState(false);
  const [isOverPopover, setIsOverPopover] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOverButton || isOverPopover) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setOpen(true);
    } else {
      timeoutRef.current = setTimeout(() => {
        setOpen(false);
      }, 100);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOverButton, isOverPopover]);

  return (
    <>
      <Box
        ref={anchorRef}
        aria-owns={open ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        onMouseEnter={() => setIsOverButton(true)}
        onMouseLeave={() => setIsOverButton(false)}
        sx={{
          height: 1,
        }}
      >
        {children}
      </Box>

      <Popover
        id="mouse-over-popover"
        open={open}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        onClose={() => setOpen(false)}
        disableRestoreFocus
        disableAutoFocus
        disableEnforceFocus
        sx={{
          pointerEvents: 'none',
          zIndex: 10000, // 10000 is for override the default z-index of the fullcalendar
          '& .MuiPopover-paper': {
            marginLeft: theme.spacing(1.25), // Space for the triangle
          },
        }}
        slotProps={{
          paper: {
            onMouseEnter: () => setIsOverPopover(true),
            onMouseLeave: () => setIsOverPopover(false),
            sx: {
              pointerEvents: 'auto',
              boxShadow: 3,
              width: 'fit-content',
              position: 'relative',
              overflow: 'visible',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '-10px',
                marginTop: '-10px',
                borderTop: '10px solid transparent',
                borderBottom: '10px solid transparent',
                borderRight: `10px solid ${theme.palette.primary.medium}`,
                zIndex: 1,
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '-12px',
                marginTop: '-12px',
                borderTop: '12px solid transparent',
                borderBottom: '12px solid transparent',
                borderRight: '12px solid transparent',
                zIndex: 0,
              },
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacers.xxs,
            px: theme.spacers.m,
            py: theme.spacers.s,
            backgroundColor: theme.palette.primary.medium,
            color: theme.palette.common.white,
            borderRadius: theme.spacers.xs,
          }}
        >
          <Typography
            variant="body3"
            sx={{ fontWeight: theme.typography.fontWeightMedium }}
          >
            {title}
          </Typography>
          <Typography variant="body3">{timeRange}</Typography>
          {reccuringEvent && (
            <Typography variant="body3">
              {formatRecurringEvent(reccuringEvent)}
            </Typography>
          )}
          <Typography
            variant="body3"
            sx={{
              fontWeight: theme.typography.fontWeightMedium,
              mt: theme.spacers.m,
            }}
          >
            {bookings.length}/{capacity} reservations
          </Typography>
          {bookings.map((booking, index) => (
            <Link
              variant="body2"
              color={theme.palette.common.white}
              sx={{
                fontSize: theme.typography.body3.fontSize,
              }}
              key={`booking-${booking.consumerId}-${index}`}
            >
              {booking.name}
            </Link>
          ))}
        </Box>
      </Popover>
    </>
  );
};
